// French-Canadian formatting for the share list. French takes the singular
// at zero ("0 fichier", "1 fichier", "2 fichiers") — Intl.PluralRules already
// knows this, so counts are never hand-pluralized.

const LOCALE = "fr-CA";

const plural = new Intl.PluralRules(LOCALE);
const TZ = "America/Montreal";

const dateFmt = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: TZ,
});

const shortDateFmt = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "short",
  timeZone: TZ,
});

const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: TZ,
});

const dateLongFmt = new Intl.DateTimeFormat(LOCALE, {
  dateStyle: "full",
  timeZone: TZ,
});

const timeFmt = new Intl.DateTimeFormat(LOCALE, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZone: TZ,
});

const clockFmt = new Intl.DateTimeFormat(LOCALE, {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: TZ,
});

// en-CA formats as YYYY-MM-DD — a sortable calendar-day key in Montreal time.
const dayKeyFmt = new Intl.DateTimeFormat("en-CA", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: TZ,
});

const numberFr = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });

export function formatNumber(value: number): string {
  return numberFr.format(value);
}

const KO = 1024;

/** "45,2 Mo" — fr-CA gives the comma decimal and the narrow space. */
export function formatBytes(bytes: number): string {
  if (bytes < KO) return `${bytes} o`;
  const units = ["Ko", "Mo", "Go", "To"];
  let value = bytes / KO;
  let i = 0;

  while (value >= KO && i < units.length - 1) {
    value /= KO;
    i++;
  }

  return `${numberFr.format(value)} ${units[i]}`;
}

/** Up to two initials from a name ("Jean Tremblay" → "JT"). */
export function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function formatCount(n: number, one: string, other: string) {
  return `${n} ${plural.select(n) === "one" ? one : other}`;
}

export const formatFiles = (n: number) => formatCount(n, "fichier", "fichiers");
export const formatSubscribers = (n: number) => formatCount(n, "abonné", "abonnés");
export const formatMembers = (n: number) => formatCount(n, "membre", "membres");

export const formatDate = (d: Date) => dateFmt.format(d);

/** "4 juin 2026, 12 h 37" — the only date+time format in the app. */
export const formatDateTime = (d: Date) => dateTimeFmt.format(d);

/** "12 h 37 min 05 s" — time-of-day for live logs and execution timelines. */
export const formatTime = (d: Date) => timeFmt.format(d);
export const formatDateLong = (d: Date) => dateLongFmt.format(d);

/** "14 h 32" — time of day in dense rows; the full date sits one hover away. */
export const formatClock = (d: Date) => clockFmt.format(d);

/** "2026-09-25" — the Montreal calendar day, for grouping rows by day. */
export const formatDayKey = (d: Date) => dayKeyFmt.format(d);

/**
 * Compact countdown for dense surfaces: "5 j", "3 mois", "2 ans".
 * One rule everywhere — relative countdown is the visible value, the
 * absolute date is always one hover (tooltip) away.
 */
export function formatCountdown(days: number) {
  if (days < 0) return "expiré";

  if (days === 0) return "aujourd'hui";

  if (days < 45) return `${days} j`;

  if (days < 365) return `${Math.round(days / 30)} mois`;
  const years = days / 365;

  // fr-CA decimal comma, and French stays singular below 2 ("1,5 an").
  return years < 2 ? `${numberFr.format(years)} an` : `${Math.round(years)} ans`;
}

/** Short chart tick; the associated tooltip supplies the full date. */
export const formatDateShort = (date: Date) => shortDateFmt.format(date);
